import React, { useRef, useState, useEffect, useCallback } from 'react';

// Literal Tailwind class strings per color — kept whole (not built from a
// template string) so the JIT scanner picks up every variant regardless of
// which `color` prop a given page passes at runtime.
const ARROW_COLOR_CLASSES = {
    indigo: 'bg-indigo-600 hover:bg-indigo-700',
    orange: 'bg-orange-600 hover:bg-orange-700',
    purple: 'bg-purple-600 hover:bg-purple-700',
};

// Reusable horizontal card carousel. Wraps whatever card elements the caller
// already builds (unchanged) in a native-scroll flex row plus left/right
// arrow buttons. Native touch-scroll and mouse-drag-free swipe both keep
// working since the scroll container is just a normal `overflow-x-auto` div
// — the arrows are a convenience on top of that, not a replacement for it.
const CardCarousel = ({ children, color = 'indigo' }) => {
    const scrollRef = useRef(null);
    const [canScrollLeft, setCanScrollLeft] = useState(false);
    const [canScrollRight, setCanScrollRight] = useState(false);

    const updateScrollState = useCallback(() => {
        const el = scrollRef.current;
        if (!el) return;
        setCanScrollLeft(el.scrollLeft > 4);
        setCanScrollRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 4);
    }, []);

    useEffect(() => {
        updateScrollState();
        const el = scrollRef.current;
        if (!el) return;
        el.addEventListener('scroll', updateScrollState, { passive: true });
        window.addEventListener('resize', updateScrollState);
        return () => {
            el.removeEventListener('scroll', updateScrollState);
            window.removeEventListener('resize', updateScrollState);
        };
        // Re-check after the card list itself changes (e.g. data finishes
        // loading and cards go from 0 to N) — a fresh mount may start with
        // content narrower than the container, where canScrollRight should
        // stay false rather than whatever it was computed as at 0 cards.
        // updateScrollState is stable (useCallback with an empty dep array),
        // so including it here doesn't cause extra re-runs.
    }, [children, updateScrollState]);

    const scroll = (direction) => {
        const el = scrollRef.current;
        if (!el) return;
        el.scrollBy({ left: direction * el.clientWidth * 0.9, behavior: 'smooth' });
    };

    const cards = React.Children.toArray(children);
    const arrowColorClass = ARROW_COLOR_CLASSES[color] || ARROW_COLOR_CLASSES.indigo;

    if (cards.length === 0) return null;

    return (
        <div className="relative">
            <button
                type="button"
                onClick={() => scroll(-1)}
                disabled={!canScrollLeft}
                aria-label="Scroll left"
                className={`hidden sm:flex absolute -left-4 top-1/2 -translate-y-1/2 z-10 items-center justify-center w-10 h-10 rounded-full text-white shadow-lg transition-all duration-200 ${arrowColorClass} ${!canScrollLeft ? 'opacity-0 pointer-events-none' : 'opacity-100'}`}
            >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
                </svg>
            </button>

            <div
                ref={scrollRef}
                className="flex gap-6 overflow-x-auto scroll-smooth snap-x snap-mandatory pb-2 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
            >
                {cards.map((card, index) => (
                    <div key={index} className="snap-start shrink-0 w-[85%] sm:w-[46%] md:w-[31%] lg:w-[23.5%]">
                        {card}
                    </div>
                ))}
            </div>

            <button
                type="button"
                onClick={() => scroll(1)}
                disabled={!canScrollRight}
                aria-label="Scroll right"
                className={`hidden sm:flex absolute -right-4 top-1/2 -translate-y-1/2 z-10 items-center justify-center w-10 h-10 rounded-full text-white shadow-lg transition-all duration-200 ${arrowColorClass} ${!canScrollRight ? 'opacity-0 pointer-events-none' : 'opacity-100'}`}
            >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                </svg>
            </button>
        </div>
    );
};

export default CardCarousel;
